import { useCallback } from 'react';

import * as Sentry from '@sentry/react';

const UNITY_SENDMESSAGE_ERROR_PATTERN =
    /Method not found|Error invoking post|invoking post/i;

/**
 * Wraps Unity sendMessage with try/catch. Catches Unity WebGL "Method not found"
 * and "Error invoking post" errors (common in Android WebView) and reports them
 * as Sentry warnings instead of letting them propagate as uncaught errors.
 *
 * @param {Function} sendMessage - The sendMessage function from useUnityContext
 * @returns {Function} A safe sendMessage that catches and reports known Unity errors
 */
export const useSafeSendMessage = (sendMessage) => {
    return useCallback(
        (gameObjectName, methodName, parameter) => {
            try {
                sendMessage(gameObjectName, methodName, parameter);
            } catch (e) {
                const msg = e?.message || String(e);
                if (UNITY_SENDMESSAGE_ERROR_PATTERN.test(msg)) {
                    Sentry.withScope((scope) => {
                        scope.setLevel('warning');
                        scope.setTag('game_object', gameObjectName);
                        scope.setTag('method', methodName);
                        Sentry.captureMessage(
                            `Unity SendMessage failed: ${msg}`,
                            'warning'
                        );
                    });
                } else {
                    throw e;
                }
            }
        },
        [sendMessage]
    );
};
